import {
  ACTIONS,
  SCREEN_ACCESS_ACTION_ORDER,
  groupScreensForAccess,
  normalizeScreenPermissionRow,
  syncModuleFlagsFromScreens,
} from "../../../rbac/catalog";

export const ACTION_SHORT_LABELS = {
  view: "View",
  create: "Create",
  import: "Import",
  edit: "Edit",
  approve: "Approve",
  export: "Export",
  delete: "Delete",
};

export const ACTION_HINTS = {
  view: "Open the screen. Required before any other permission on that screen.",
  create: "Add new records or run the screen's create / upload step.",
  import: "Upload files into this screen.",
  edit: "Change saved records or settings.",
  approve: "Approve, finalise or reject requests and runs.",
  export: "Download Excel / PDF output.",
  delete: "Delete, deactivate or close records.",
};

/** Plain-language Data Access levels (mirror backend `dataScope.js`). */
export const DATA_ACCESS_LEVELS = [
  { value: "own", label: "Own record only", hint: "Only the user's own employee data." },
  {
    value: "team",
    label: "Reporting team",
    hint: "Their own data plus everyone who reports to them, directly or indirectly.",
  },
  {
    value: "department",
    label: "Selected departments",
    hint: "Employees in the departments you pick below.",
  },
  {
    value: "branch_location",
    label: "Selected branches / locations",
    hint: "Employees in the branches you pick below.",
  },
  { value: "all", label: "Entire company", hint: "Every employee in the company." },
];

export const LEVELS_WITH_ITEMS = new Set(["department", "branch_location"]);

export const dataLevelLabel = (level) =>
  DATA_ACCESS_LEVELS.find((l) => l.value === level)?.label || "Not set";

export const orderedActions = (screen) =>
  SCREEN_ACCESS_ACTION_ORDER.filter((a) => (screen?.actions || []).includes(a));

export const apiErrorMessage = (err, fallback) =>
  err?.response?.data?.message || err?.message || fallback;

/** JSON columns can arrive as strings depending on the MySQL driver. */
export const parseMaybeJson = (value) => {
  if (typeof value !== "string") return value ?? null;
  try {
    return JSON.parse(value);
  } catch {
    return null;
  }
};

/** Stored Data Access → `{ level, selectedItems:number[] }` ("" level when unset). */
export const normalizeData = (raw) => {
  const d = parseMaybeJson(raw) || {};
  return {
    level: typeof d.level === "string" ? d.level : "",
    selectedItems: LEVELS_WITH_ITEMS.has(d.level)
      ? (d.selectedItems || []).map(Number).filter(Number.isFinite)
      : [],
  };
};

// ---------------------------------------------------------------------------
// Role classification
// ---------------------------------------------------------------------------

const DEFAULT_SYSTEM_ROLE_CODES = new Set(["EMPLOYEE", "MANAGER"]);
const DEFAULT_SYSTEM_ROLE_NAMES = new Set(["employee / self", "employee/self", "reporting manager"]);

/** True only for the seeded Employee / Self and Reporting Manager roles. */
export function isDefaultSystemRole(role) {
  if (!role) return false;
  const code = String(role.code || "").trim().toUpperCase().replace(/\s+/g, "_");
  if (DEFAULT_SYSTEM_ROLE_CODES.has(code)) return true;
  const name = String(role.name || "").trim().toLowerCase().replace(/\s+/g, " ");
  return DEFAULT_SYSTEM_ROLE_NAMES.has(name);
}

export function isSystemRole(role) {
  if (!role) return false;
  return (
    isDefaultSystemRole(role) ||
    role.isSystem === true ||
    role.isSystemDefaultRole === true ||
    String(role.type || "").toLowerCase() === "system"
  );
}

/** Maps API / legacy role shapes to "access_based" | "action_based". */
export function normalizeRoleWizardType(role) {
  if (!role || typeof role !== "object") return "action_based";
  if (role.accessBased === true || role.access_based === true || role.use_access_based === true) {
    return "access_based";
  }
  const raw =
    role.roleType ?? role.role_type ?? role.RoleType ?? role.roletype ?? role.permissionMode ?? role.permission_mode;
  if (raw == null || raw === "") return "action_based";
  const s = String(raw).trim().toLowerCase().replace(/\s+/g, "_").replace(/-/g, "_");
  return s === "access_based" || s === "accessbased" || s === "access" ? "access_based" : "action_based";
}

/** Screens / data access apply to Access Based custom roles and to system roles. */
export const roleUsesAccess = (role) =>
  isSystemRole(role) || normalizeRoleWizardType(role) === "access_based";

// ---------------------------------------------------------------------------
// Assignment
// ---------------------------------------------------------------------------

/** Mirrors backend `extractTargetUserIdsFromFilter` so the UI blocks the same empty selections. */
export function assignableEmployeeIds(filterData) {
  const employees = Array.isArray(filterData?.employeesData) ? filterData.employeesData : [];
  const ids = new Set();
  for (const emp of employees) {
    if (emp?.userId) ids.add(Number(emp.userId));
    else if (emp?.user?.id) ids.add(Number(emp.user.id));
    else if (emp?.id && !emp?.email) ids.add(Number(emp.id));
  }
  return [...ids].filter((id) => Number.isFinite(id) && id > 0).sort((a, b) => a - b);
}

export const countAssignableEmployees = (filterData) => assignableEmployeeIds(filterData).length;

// ---------------------------------------------------------------------------
// Screen access shape helpers (stored shape matches src/rbac/catalog.js)
// ---------------------------------------------------------------------------

/**
 * Keeps only `true` flags on declared screens; module `view` follows its screens.
 * Screens that are present but fully unchecked are stored as `{}` so expand*
 * migration heuristics do not re-seed revoked Action screens after a role edit.
 */
export function sanitizeScreenAccessForSubmit(access) {
  const source = access && typeof access === "object" ? access : {};
  const cleaned = {};
  for (const [moduleKey, moduleValue] of Object.entries(source)) {
    if (!moduleValue || typeof moduleValue !== "object") continue;
    const screens = {};
    for (const [screenKey, screenValue] of Object.entries(moduleValue.screens || {})) {
      if (!screenValue || typeof screenValue !== "object") continue;
      const perms = {};
      for (const action of ACTIONS) if (screenValue[action] === true) perms[action] = true;
      screens[screenKey] = perms;
    }
    const hasGrant = Object.values(screens).some((scr) => Object.keys(scr).length > 0);
    if (!hasGrant) continue;
    const moduleObj = { screens };
    if (Object.values(screens).some((scr) => !!scr.view)) moduleObj.view = true;
    cleaned[moduleKey] = moduleObj;
  }
  return cleaned;
}

export const screenGrantCount = (perms, screen) =>
  orderedActions(screen).filter((a) => perms?.[a] === true).length;

/** Totals for one module: granted / total declared actions and the overall state. */
export function moduleStats(moduleDef, moduleAcc) {
  let granted = 0;
  let total = 0;
  let screensOn = 0;
  for (const screen of moduleDef?.screens || []) {
    const n = screenGrantCount(moduleAcc?.screens?.[screen.key], screen);
    granted += n;
    total += orderedActions(screen).length;
    if (n > 0) screensOn += 1;
  }
  const state = granted === 0 ? "none" : granted === total ? "full" : "partial";
  return { granted, total, screensOn, screenTotal: moduleDef?.screens?.length || 0, state };
}

/** Matrix cell state for one action across a set of screens: na | none | partial | full. */
export function actionState(screens, moduleAcc, action) {
  const declaring = (screens || []).filter((s) => (s.actions || []).includes(action));
  if (declaring.length === 0) return "na";
  const on = declaring.filter((s) => moduleAcc?.screens?.[s.key]?.[action] === true).length;
  return on === 0 ? "none" : on === declaring.length ? "full" : "partial";
}

export const isModuleGranted = (moduleDef, moduleAcc) => moduleStats(moduleDef, moduleAcc).granted > 0;

const cloneModule = (acc) => ({ ...(acc || {}), screens: { ...(acc?.screens || {}) } });

/**
 * Applies a preset to some screens of a module.
 * preset: "none" | "view" (View only) | "full" (every declared action).
 */
export function applyScreenPreset(moduleAcc, moduleDef, screenKeys, preset) {
  const next = cloneModule(moduleAcc);
  const keys = new Set(screenKeys);
  for (const screen of moduleDef.screens) {
    if (!keys.has(screen.key)) continue;
    const row = {};
    for (const action of ACTIONS) {
      const declared = screen.actions.includes(action);
      row[action] = preset === "full" ? declared : preset === "view" ? declared && action === "view" : false;
    }
    next.screens[screen.key] = normalizeScreenPermissionRow(
      row,
      screen.actions,
      preset === "none" ? { preferViewOff: true } : {}
    );
  }
  syncModuleFlagsFromScreens(next, moduleDef);
  return next;
}

/** Toggles one action on one screen, keeping the "View is required" rule. */
export function toggleScreenAction(moduleAcc, moduleDef, screenKey, action, value) {
  const screen = moduleDef.screens.find((s) => s.key === screenKey);
  if (!screen) return moduleAcc;
  const next = cloneModule(moduleAcc);
  next.screens[screenKey] = normalizeScreenPermissionRow(
    { ...(next.screens[screenKey] || {}), [action]: !!value },
    screen.actions,
    action === "view" && !value ? { preferViewOff: true } : {}
  );
  syncModuleFlagsFromScreens(next, moduleDef);
  return next;
}

/** Lower-cased text used to search a module by its own or its screens' names. */
export function moduleSearchHits(moduleDef, query) {
  const q = String(query || "").trim().toLowerCase();
  if (!q) return { matches: true, screens: [] };
  const own = `${moduleDef.label} ${moduleDef.description || ""}`.toLowerCase().includes(q);
  const screens = moduleDef.screens.filter((s) =>
    `${s.label} ${s.description || ""} ${s.flowGroup || ""}`.toLowerCase().includes(q)
  );
  return { matches: own || screens.length > 0, screens };
}

export const moduleSections = (moduleDef) =>
  groupScreensForAccess(moduleDef.screens)
    .map((g) => g.label)
    .filter(Boolean);

// ---------------------------------------------------------------------------
// Delegation (mirrors backend `assertDataAccessWithinScope`)
// ---------------------------------------------------------------------------

/** Data Access levels a delegate with `ownScope` may grant. Admins (null) may grant all. */
export function allowedDataLevels(ownScope) {
  if (!ownScope || ownScope.level === "all") return new Set(DATA_ACCESS_LEVELS.map((l) => l.value));
  const out = new Set(["own"]);
  if (ownScope.level === "team") out.add("team");
  if (LEVELS_WITH_ITEMS.has(ownScope.level)) out.add(ownScope.level);
  return out;
}

/** Order-independent equality for sanitized screen access objects. */
export function sameScreenAccess(a, b) {
  const keys = new Set();
  JSON.stringify([a, b], (k, v) => {
    keys.add(k);
    return v;
  });
  const order = [...keys].sort();
  return JSON.stringify(a || {}, order) === JSON.stringify(b || {}, order);
}

export const sameDataAccess = (a, b) =>
  (a?.level || "") === (b?.level || "") &&
  JSON.stringify([...(a?.selectedItems || [])].map(Number).sort()) ===
    JSON.stringify([...(b?.selectedItems || [])].map(Number).sort());
